import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { PermissionLevels, Site } from '@debbie/common/dist';
import THEME from '../theme';
import Tooltip from './Tooltip';
import UserType from './UserType';
import { c, h } from '@debbie/cortex/dist';
import PlaceholderLoader from './PlaceholderLoader';
import CreditorOrTenant from './CreditorOrTenant';
import { selectTenantId } from '@debbie/cortex/dist/selectedOwners/selectors';
import { TenantStatus } from '@debbie/common/dist/tenants';
import { ExternalLink, Inbox, Phone } from './Icons';
import { selectRole } from '@debbie/cortex/dist/me/selectors';
import TenantStatusBadge from './TenantStatusBadge';
import { useCreditor } from '@debbie/cortex/dist/creditors';
const UserPreview = (props) => {
    const { user, style, active, onClick } = props;
    const site = useSelector((s) => s.site);
    const [hovered, setHovered] = useState(false);
    const [showTooltipOnTop, setShowTooltipOnTop] = useState(false);
    const ref = useRef(null);
    const onMouseIn = () => {
        setHovered(true);
    };
    const onMouseOut = () => {
        setHovered(false);
    };
    if (!user) {
        return null;
    }
    return (React.createElement(UserPreviewStyled, { ref: ref, onMouseEnter: onMouseIn, onMouseLeave: onMouseOut, style: style, onClick: onClick, active: active, user: user },
        React.createElement("div", { className: "user-short" },
            user.firstName && user.firstName.slice(0, 1),
            user.lastName && user.lastName.slice(0, 1)),
        hovered && !props.noTooltip && user.id && site === Site.Caseworker && (React.createElement(Tooltip, { tooltipToLeft: props.tooltipToLeft, parentRef: ref, setShowTooltipOnTop: setShowTooltipOnTop, shouldShowOnTop: showTooltipOnTop, height: 300, style: { width: '350px' } },
            React.createElement(UserPreviewTooltip, { userId: user.id })))));
};
export default UserPreview;
const size = 27;
const getBackgroundColor = (user) => {
    switch (user.role) {
        case PermissionLevels.admin:
        case PermissionLevels.superAdmin:
            return '#D07474';
        case PermissionLevels.client:
            return '#7773E8';
        case PermissionLevels.debtor:
            return '#ED9E38';
        default:
            return '#eec125';
    }
};
const UserPreviewStyled = styled.div `
  width: ${size}px;
  height: ${size}px;
  display: inline-block;
  border-radius: 100%;
  margin-right: 5px;
  background-color: ${(props) => getBackgroundColor(props.user)};
  position: relative;

  & > div.user-short {
    text-align: center;
    line-height: 27px;
    color: ${THEME.COLOR_SECONDARY};
    font-size: 11px;
    width: 100%;
  }

  ${(props) => props.active
    ? `
      &::before {
        content: ' ';
        border: 2px solid ${THEME.COLOR_ACCENT};
        width: 35px;
        height: 35px;
        border-radius: 100%;
        display: inline-block;
        position: absolute;
        left: -4px;
        top: -4px;
      }
    `
    : ''}
`;
const UserPreviewTooltip = (props) => {
    var _a;
    const users = useSelector((s) => s.users);
    const site = useSelector((s) => s.site);
    const lang = useSelector((s) => s.lang);
    const tenantId = useSelector(selectTenantId);
    const meRole = useSelector(selectRole);
    const userLoadable = users[props.userId];
    const user = h.hasData(userLoadable) ? userLoadable.data : null;
    useEffect(() => {
        if (!user) {
            c.users.getById(props.userId);
        }
    }, [props.userId]);
    return h.isLoading(userLoadable) ? (React.createElement(PlaceholderLoader, null)) : user ? (React.createElement("div", null,
        site === Site.Caseworker && (React.createElement("div", null,
            React.createElement("p", { style: {
                    fontSize: '0.7em',
                    fontWeight: 900,
                    margin: '0px',
                    color: THEME.DARK_COLOR_SECONDARY,
                    float: 'right',
                } }, moment(user.createdAt).format(THEME.DATE_FORMAT_LONG)),
            user.details.meetingAccountType === 'meeting_only' ? null : (React.createElement(UserType, { role: user.role })))),
        React.createElement("h3", { style: { marginBottom: '.5em' } },
            user.firstName,
            " ",
            user.lastName,
            ' ',
            site === Site.Caseworker &&
                ['admin', 'caseworker', 'client'].includes(user.role) &&
                meRole === 'admin' && (React.createElement(ExternalLink, { style: { cursor: 'pointer' }, onClick: () => {
                    c.redirect.redirect(`/settings/users/${user.id}`);
                } }))),
        site === Site.Caseworker && (React.createElement("div", null,
            React.createElement("p", { style: {
                    margin: '0 0 30px 0',
                    fontWeight: '400',
                    fontSize: '0.8em',
                } },
                user.email && (React.createElement("a", { style: { color: 'inherit', textDecoration: 'none' }, href: `mailto:${user.email}` },
                    React.createElement(Inbox, { style: { margin: '0.2em 0 -0.2em 0' } }),
                    " ",
                    user.email)),
                React.createElement("br", null),
                ((_a = user.phone) === null || _a === void 0 ? void 0 : _a.number) ? (React.createElement("a", { style: { color: 'inherit', textDecoration: 'none' }, href: `tel:${user.phone.locale}${user.phone.number}` },
                    React.createElement(Phone, { style: { margin: '0.2em 0 -0.2em 0' } }),
                    ' ',
                    user.phone.locale ? React.createElement("span", null,
                        "+",
                        user.phone.locale,
                        " ") : null,
                    ' ',
                    user.phone.number)) : null))),
        site === Site.Caseworker &&
            ['admin', 'caseworker'].includes(user.role) &&
            user.tenants &&
            !!user.tenants.length ? (React.createElement("div", null, user.tenants.map((tenant) => {
            return (React.createElement(CreditorOrTenant, { key: tenant.id, active: tenant.id === tenantId, name: tenant.name, description: React.createElement(React.Fragment, null,
                    React.createElement("b", null, tenant.countryCode),
                    " ",
                    tenant.name), badge: tenant.status !== TenantStatus.Active ? (React.createElement(TenantStatusBadge, { status: tenant.status, style: { float: 'right' } })) : undefined }));
        }))) : null,
        ['client'].includes(user.role) &&
            user.creditors &&
            !!user.creditors.length ? (React.createElement("div", null, user.creditors.map((creditor) => {
            return React.createElement(CreditorPreview, { creditorId: creditor.id });
        }))) : null)) : null;
};
const CreditorPreview = (props) => {
    const creditorLoadable = useCreditor(props.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const selectedCreditorId = useSelector((s) => s.selectedCreditor);
    if (!creditor) {
        return null;
    }
    return (React.createElement(CreditorOrTenant, { key: creditor.id, active: creditor.creditorId === selectedCreditorId, name: creditor.name, description: React.createElement(React.Fragment, null, creditor.name) }));
};
//# sourceMappingURL=UserPreview.js.map